import React, { useState } from 'react';
import { DownloadIcon, EyeIcon, PlusIcon, PrinterIcon, SearchIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Quotation, WalkInClient } from '../../types';
import { useData } from '../../store/DataContext';
import { BillingDocumentPreviewModal, DocumentPreviewAction } from './BillingDocumentPreviewModal';
import { BillingSection } from './BillingModule';
import { formatCurrency } from './utils';
interface Props {
  onNavigate: (section: BillingSection) => void;
}
export const Quotations: React.FC<Props> = ({
  onNavigate
}) => {
  const {
    quotations,
    patients,
    walkInClients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
  const [previewAction, setPreviewAction] = useState<DocumentPreviewAction>('view');
  const openPreview = (quotation: Quotation, action: DocumentPreviewAction) => {
    setPreviewAction(action);
    setSelectedQuotation(quotation);
  };
  const filteredQuotations = quotations.filter(q => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    if (q.quotationNumber.toLowerCase().includes(query)) return true;
    let customerName = '';
    if (q.customerType === 'Patient') {
      const p = patients.find(p => p.id === q.customerId);
      if (p) customerName = `${p.firstName} ${p.lastName}`;
    } else {
      const c = walkInClients.find(c => c.id === q.customerId);
      if (c) customerName = c.name;
    }
    return customerName.toLowerCase().includes(query);
  });
  const getCustomerName = (customerId: string, type: 'Patient' | 'WalkIn') => {
    if (type === 'Patient') {
      const p = patients.find(p => p.id === customerId);
      return p ? `${p.firstName} ${p.lastName}` : 'Unknown Patient';
    } else {
      const c = walkInClients.find(c => c.id === customerId);
      return c ? c.name : 'Unknown Client';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Accepted':
        return 'bg-green-100 text-green-800';
      case 'Declined':
        return 'bg-red-100 text-red-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Invoiced':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-1635">
      <div className="flex justify-between items-center" data-id="element-1636">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1637">Quotations</h1>
        <Button onClick={() => onNavigate('new-quotation' as any)} data-id="element-1638">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-1639" /> New Quotation
        </Button>
      </div>

      <Card noPadding data-id="element-1640">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1641">
          <div className="relative max-w-md" data-id="element-1642">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1643">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1644" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by quotation number or customer name..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1645" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1646">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1647">
            <thead className="bg-gray-50" data-id="element-1648">
              <tr data-id="element-1649">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1650">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1651">
                  Quotation#
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1652">
                  Customer
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1653">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1654">
                  Amount
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1655">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1656">
              {filteredQuotations.length > 0 ? filteredQuotations.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map(quo => <tr key={quo.id} className="hover:bg-gray-50" data-id="element-1657">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1658">
                        {new Date(quo.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-1659">
                        {quo.quotationNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1660">
                        {getCustomerName(quo.customerId, quo.customerType)}
                        <span className="ml-2 text-xs text-gray-400 font-normal" data-id="element-1661">
                          ({quo.customerType})
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1662">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(quo.status)}`} data-id="element-1663">
                          {quo.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1664">
                        {formatCurrency(quo.totalAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1665">
                        <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500" title="View quotation" aria-label={`View quotation ${quo.quotationNumber}`} onClick={() => openPreview(quo, 'view')} data-id="element-1666">
                          <EyeIcon className="h-4 w-4" data-id="element-1667" />
                        </button>
                        <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Print quotation" aria-label={`Print quotation ${quo.quotationNumber}`} onClick={() => openPreview(quo, 'print')} data-id="element-1668">
                          <PrinterIcon className="h-4 w-4" data-id="element-1669" />
                        </button>
                        <button type="button" className="mx-1 rounded p-1 text-gray-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Download quotation PDF" aria-label={`Download quotation ${quo.quotationNumber} as PDF`} onClick={() => openPreview(quo, 'download')} data-id="element-1670">
                          <DownloadIcon className="h-4 w-4" data-id="element-1671" />
                        </button>
                      </td>
                    </tr>) : <tr data-id="element-1672">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500" data-id="element-1673">
                    No quotations found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedQuotation && <BillingDocumentPreviewModal record={selectedQuotation} documentType="Quotation" customer={getQuotationCustomer(selectedQuotation, patients, walkInClients)} initialAction={previewAction} onClose={() => setSelectedQuotation(null)} data-id="element-1674" />}
    </div>;
};
function getQuotationCustomer(quotation: Quotation, patients: Patient[], walkInClients: WalkInClient[]) {
  if (quotation.customerType === 'Patient') {
    const patient = patients.find(entry => entry.id === quotation.customerId);
    return {
      name: patient ? `${patient.firstName} ${patient.lastName}` : 'Registered patient',
      fileNumber: patient?.bmcFileNumber,
      address: patient?.address,
      contact: patient?.contact
    };
  }
  const walkInClient = walkInClients.find(entry => entry.id === quotation.customerId);
  return {
    name: walkInClient?.name || 'Walk-in client',
    address: walkInClient?.address,
    contact: walkInClient?.phone
  };
}